import { db } from "@/firebase";
import { Category, CategoryFormData } from "@/model/entities";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  orderBy,
} from "firebase/firestore";

export const categoryService = {
  // Create a new category with image upload
  create: async (
    restaurantId: string,
    categoryData: CategoryFormData,
    order: number = 0
  ): Promise<Category> => {
    const categoriesRef = collection(db, "categories");
    const newCategoryRef = doc(categoriesRef);
    const newCategoryId = newCategoryRef.id;
    const newCategory = { ...categoryData, restaurantId, order } as Category;
    await setDoc(newCategoryRef, newCategory);
    return { id: newCategoryId, ...newCategory };
  },

  // Get all categories for a restaurant
  fetchAll: async (restaurantId: string): Promise<Category[]> => {
    const categoriesRef = collection(db, "categories");
    // Order by the 'order' field so the client receives categories in the persisted order
    const q = query(
      categoriesRef,
      where("restaurantId", "==", restaurantId),
      orderBy("order", "asc")
    );
    const snapshot = await getDocs(q);
    const categories: Category[] = snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Category
    );
    return categories;
  },

  // Update a category with optional image upload
  update: async (
    categoryId: string,
    categoryData: CategoryFormData
  ): Promise<Category> => {
    const categoryRef = doc(db, "categories", categoryId);
    await updateDoc(categoryRef, { ...categoryData });
    const snapshot = await getDoc(categoryRef);
    return { id: snapshot.id, ...(snapshot.data() as Category) };
  },

  updateBulk: async (categories: Category[]) => {
    const categoriesToUpdate = categories.map((category, index) => {
      return { id: category.id, ...category, order: index };
    });

    const batch = writeBatch(db);

    categoriesToUpdate.forEach((category) => {
      if (typeof category.id === "string") {
        const categoryRef = doc(db, "categories", category.id);
        batch.update(categoryRef, { ...category });
      }
    });

    await batch.commit();
  },

  // Delete a category and all its menu items
  delete: async (categoryId: string): Promise<boolean> => {
    try {
      // First, delete all menu items in this category
      const menuItemsRef = collection(db, "menuItems");
      const menuItemsQuery = query(
        menuItemsRef,
        where("categoryId", "==", categoryId)
      );
      const menuItemsSnapshot = await getDocs(menuItemsQuery);

      if (!menuItemsSnapshot.empty) {
        const batch = writeBatch(db);
        menuItemsSnapshot.docs.forEach((menuItemDoc) => {
          batch.delete(menuItemDoc.ref);
        });
        await batch.commit();
        console.log(
          `Deleted ${menuItemsSnapshot.size} menu items from category ${categoryId}`
        );
      }

      // Then delete the category itself
      const categoryRef = doc(db, "categories", categoryId);
      await deleteDoc(categoryRef);
      return true;
    } catch (error) {
      console.error(`Error deleting category ${categoryId}:`, error);
      throw error;
    }
  },

  // Toggle hidden state of a category
  toggleHidden: async (
    categoryId: string,
    isHidden: boolean
  ): Promise<Category> => {
    const categoryRef = doc(db, "categories", categoryId);
    await updateDoc(categoryRef, { isHidden });
    const snapshot = await getDoc(categoryRef);
    return { id: snapshot.id, ...(snapshot.data() as Category) };
  },
};
